import React from 'react';
import { useTheme } from '../context/ThemeContext';

const FilterSection = ({ 
  clientFilter, 
  setClientFilter, 
  hideZeroCompetitors, 
  setHideZeroCompetitors, 
  sortBy, 
  setSortBy,
  searchQuery,
  setSearchQuery,
  filteredCount,
  totalCount,
  onClearFilters,
  hideSearch = false,
  viewMode = 'detailed'
}) => {
  const { isDark } = useTheme();
  const getFilterIcon = (type) => {
    const icons = {
      visibility: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      competitors: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      sort: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
      ),
      search: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
    };
    return icons[type];
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (clientFilter !== 'all') count++;
    if (hideZeroCompetitors) count++;
    if (sortBy !== 'position') count++; // position is now the default
    if (!hideSearch && searchQuery && searchQuery.trim()) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="glass-card rounded-xl">
      {/* Header with title and active filter indicator */}
      <div className={`border-b px-6 py-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className={`heading-mono text-lg font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Filter Rankings</h3>
            {activeFilterCount > 0 && (
              <span className="glass-badge-success text-mono text-xs font-medium px-2.5 py-0.5 rounded-full">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Showing <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{filteredCount}</span> of <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{totalCount}</span> results
            </span>
            {activeFilterCount > 0 && (
              <button
                onClick={onClearFilters}
                className={`text-mono text-sm flex items-center gap-1 btn-glass px-3 py-1 rounded-lg hover:scale-105 transform transition-all`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className={viewMode === 'simple' ? 'p-12' : 'p-16'}>
        <div className={`grid grid-cols-1 ${hideSearch ? 'lg:grid-cols-3' : 'lg:grid-cols-2 xl:grid-cols-4'} ${viewMode === 'simple' ? 'gap-12' : 'gap-16'}`}>
          
          {/* Search Filter - Only show if not hidden */}
          {!hideSearch && (
            <div className={viewMode === 'simple' ? 'space-y-4' : 'space-y-6'}>
              <div className="flex items-center gap-2">
                <div className={isDark ? 'text-accent-400' : 'text-gray-600'}>{getFilterIcon('search')}</div>
                <label className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Search Queries</label>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery || ''}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by query text..."
                  className="glass-input w-full pl-3 pr-8 py-2 rounded-lg text-mono text-sm placeholder-gray-400 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute right-2 top-2 hover:scale-110 transform transition-all ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Client Visibility Filter */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={isDark ? 'text-accent-400' : 'text-gray-600'}>{getFilterIcon('visibility')}</div>
              <label className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Client Visibility</label>
            </div>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="clientFilter"
                  value="all"
                  checked={clientFilter === 'all'}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="h-4 w-4 text-accent-400 focus:ring-accent-400 glass-radio"
                />
                <span className={`ml-2 text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>All results</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="clientFilter"
                  value="found"
                  checked={clientFilter === 'found'}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="h-4 w-4 text-accent-400 focus:ring-accent-400 glass-radio"
                />
                <span className={`ml-2 text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Client mentioned</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="clientFilter"
                  value="notfound"
                  checked={clientFilter === 'notfound'}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="h-4 w-4 text-accent-400 focus:ring-accent-400 glass-radio"
                />
                <span className={`ml-2 text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Client not found</span>
              </label>
            </div>
          </div>

          {/* Competitors Filter */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={isDark ? 'text-accent-400' : 'text-gray-600'}>{getFilterIcon('competitors')}</div>
              <label className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Competitors</label>
            </div>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideZeroCompetitors}
                  onChange={(e) => setHideZeroCompetitors(e.target.checked)}
                  className="h-4 w-4 text-accent-400 focus:ring-accent-400 glass-checkbox rounded"
                />
                <span className={`ml-2 text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Hide results with no competitors</span>
              </label>
            </div>
          </div>

          {/* Sort Filter */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={isDark ? 'text-accent-400' : 'text-gray-600'}>{getFilterIcon('sort')}</div>
              <label className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Sort By</label>
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="glass-select w-full px-3 py-2 rounded-lg text-mono text-sm focus:outline-none"
            >
              <option value="position">Position in text</option>
              <option value="engine">AI engine</option>
              <option value="default">Default order</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;